import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useRentalCarApi } from "../../http/rentalCars/api";
import { UpdateRentalCar } from "../../http/rentalCars/types";
import { User } from "../../http/users/types";
import ModalDialog from "../ui/ModalDialog";
import RentalCarForm, { RentalCarFormBody } from "./RentalCarForm";

type EditRentalCarDialogProps = {
  rentalCarId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
  driver?: User;
};

const EditRentalCarDialog: FC<EditRentalCarDialogProps> = ({
  rentalCarId,
  onClose,
  carId,
}: EditRentalCarDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const rentalCarsApi = useRentalCarApi();

  const rentalCarSwr = rentalCarsApi.getAdminRentalCar(rentalCarId, aborter.current.signal);
  const [rentalCar, isInitialized] = useDataInitializer(rentalCarSwr.data, rentalCarSwr.error);

  useEffect(() => {
    rentalCarId && rentalCarSwr.trigger();
  }, [rentalCarId]);

  useEffect(() => {
    rentalCarSwr.error && dispatch(showErrorNotification(i18n, rentalCarSwr.error));
  }, [rentalCarSwr.error]);

  const onSubmit = async (formData: RentalCarFormBody): Promise<void> => {
    if (rentalCar?.id) {
      const body: UpdateRentalCar = {
        carId: carId ?? formData.car?.id!,
        driverId: formData.driver.id,
        plateNumber: formData.plateNumber,
        startDate: formData.startDate,
        endDate: formData.endDate,
        leasingCompanyId: formData.leasingCompany.id,
        manufacturerId: formData.manufacturer?.id,
        model: formData.model,
      };

      setIsSubmitting(true);
      try {
        await rentalCarsApi.updateAdminRentalCar(rentalCar.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.rentalCars.edit}>
      <RentalCarForm
        onSubmit={onSubmit}
        isDisabled={isSubmitting || !rentalCar?.id || !userSettings.permissions.rentalCars.update}
        isLoading={!isInitialized}
        rentalCar={rentalCar}
        carId={carId}
      />
    </ModalDialog>
  );
};

export default EditRentalCarDialog;
